import React, { useState, useEffect } from "react";
import { BRAND } from "../data/brand";

const NAV_LINKS = [
  { label:"Features", href:"#features" },
  { label:"Pricing",  href:"#pricing"  },
  { label:"FAQ",      href:"#faq"      },
  { label:"Guide",    href:"/guide"    },
];

const FOOTER_COLS = [
  { title:"Product",  links:[{ label:"Open App", href:"/app" },{ label:"User Guide", href:"/guide" },{ label:"Pricing", href:"#pricing" }] },
  { title:"Legal",    links:[{ label:"Privacy Policy", href:"/privacy" },{ label:"Terms of Use", href:"/terms" },{ label:"Disclaimer", href:"/disclaimer" },{ label:"Refund Policy", href:"/refunds" }] },
  { title:"Company",  links:[{ label:"About DPL Homestar", href:"https://dplhomestar.com", external:true },{ label:"Contact", href:`mailto:${BRAND.email}` },{ label:"My Account", href:"/account" }] },
];

export default function MarketingLayout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <div style={{ minHeight:"100vh", background:"#07090f", color:"#8496a8", fontFamily:"'Inter','Segoe UI',sans-serif" }}>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=JetBrains+Mono:wght@400;700;800&display=swap');
        * { box-sizing: border-box; }
        a { transition: color .15s, opacity .15s; }
      `}</style>

      {/* ── Sticky nav ── */}
      <nav style={{
        position:"sticky", top:0, zIndex:100,
        background: scrolled ? "#07090fee" : "transparent",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        borderBottom: scrolled ? "1px solid #0d1520" : "1px solid transparent",
        transition:"background .3s, border-color .3s, backdrop-filter .3s",
      }}>
        <div style={{ maxWidth:1100, margin:"0 auto", padding:"0 24px", height:60, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          {/* Logo */}
          <a href="/" style={{ textDecoration:"none", display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="#3b82f6" strokeWidth="1.5"/>
                <line x1="3" y1="12" x2="21" y2="12" stroke="#3b82f6" strokeWidth="1.5"/>
                <line x1="12" y1="3" x2="12" y2="21" stroke="#3b82f6" strokeWidth="1.5"/>
                <circle cx="3" cy="3" r="1.5" fill="#e8a838"/>
                <circle cx="21" cy="3" r="1.5" fill="#e8a838"/>
                <circle cx="21" cy="21" r="1.5" fill="#e8a838"/>
                <circle cx="3" cy="21" r="1.5" fill="#e8a838"/>
              </svg>
              <span style={{ fontSize:13, fontWeight:800, letterSpacing:3, color:"#dde6f0", fontFamily:"'JetBrains Mono',monospace" }}>DPL JADE</span>
            </div>
            <span style={{ fontSize:9, color:"#2a3a4a", background:"#0c1320", border:"1px solid #131924", borderRadius:3, padding:"2px 6px", fontFamily:"'JetBrains Mono',monospace" }}>by Dcode</span>
          </a>

          {/* Desktop nav */}
          <div style={{ display:"flex", alignItems:"center", gap:32 }}>
            {NAV_LINKS.map(l => (
              <a key={l.label} href={l.href} style={{ fontSize:13, color:"#3d5068", textDecoration:"none", fontWeight:500 }}
                onMouseOver={e=>e.target.style.color="#8496a8"}
                onMouseOut={e=>e.target.style.color="#3d5068"}>
                {l.label}
              </a>
            ))}
            <a href="/app" style={{ padding:"8px 20px", background:"linear-gradient(90deg,#e8a838,#f97316)", color:"#07090f", borderRadius:7, fontSize:13, fontWeight:700, textDecoration:"none", letterSpacing:0.3 }}>
              Open App →
            </a>
          </div>
        </div>
      </nav>

      {/* ── Page content ── */}
      <main>{children}</main>

      {/* ── Footer ── */}
      <footer style={{ borderTop:"1px solid #0d1520", padding:"56px 24px 40px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr", gap:40, marginBottom:48 }}>
            {/* Brand column */}
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                <span style={{ fontSize:11, fontWeight:800, letterSpacing:3, color:"#dde6f0", fontFamily:"'JetBrains Mono',monospace" }}>DPL JADE</span>
              </div>
              <p style={{ fontSize:13, color:"#2a3a4a", lineHeight:1.8, marginBottom:16, maxWidth:260 }}>{BRAND.subTagline}</p>
              <div style={{ fontSize:11, color:"#1a2636", lineHeight:1.8 }}>
                A product of<br/>
                <a href="https://dplhomestar.com" target="_blank" rel="noreferrer" style={{ color:"#2a3a4a", textDecoration:"none", fontWeight:600 }}>{BRAND.company}</a><br/>
                <a href={`mailto:${BRAND.email}`} style={{ color:"#2a3a4a", textDecoration:"none" }}>{BRAND.email}</a>
              </div>
            </div>
            {/* Link columns */}
            {FOOTER_COLS.map(col => (
              <div key={col.title}>
                <div style={{ fontSize:9, color:"#2a3a4a", letterSpacing:2, fontWeight:800, marginBottom:16, fontFamily:"'JetBrains Mono',monospace" }}>{col.title.toUpperCase()}</div>
                {col.links.map(l => (
                  <a key={l.label} href={l.href} target={l.external?"_blank":undefined} rel={l.external?"noreferrer":undefined}
                    style={{ display:"block", fontSize:13, color:"#1e2a36", textDecoration:"none", marginBottom:10, lineHeight:1.4 }}
                    onMouseOver={e=>e.target.style.color="#4a5e70"}
                    onMouseOut={e=>e.target.style.color="#1e2a36"}>
                    {l.label}
                  </a>
                ))}
              </div>
            ))}
          </div>
          <div style={{ borderTop:"1px solid #0a1220", paddingTop:20, display:"flex", flexWrap:"wrap", justifyContent:"space-between", gap:8, fontSize:11, color:"#131924" }}>
            <span>© {BRAND.year}–{new Date().getFullYear()} {BRAND.legalName}. All rights reserved.</span>
            <span>DPL Jade is a product of <a href="https://dplhomestar.com" target="_blank" rel="noreferrer" style={{ color:"#1e2a36", textDecoration:"none" }}>DPL Homestar</a> · <a href="https://dplhomestar.com" target="_blank" rel="noreferrer" style={{ color:"#1e2a36", textDecoration:"none" }}>dplhomestar.com</a></span>
          </div>
        </div>
      </footer>
    </div>
  );
}
